import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Calendar, ChevronLeft, ChevronRight, Loader2, AlertCircle } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { apiClient } from "../services/api";
import { toast } from "sonner";

const startOfWeek = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const isoDate = (d) => d.toISOString().split("T")[0];

const MonPlanningPerso = () => {
  const { t, i18n } = useTranslation();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));

  const employeeQ = useQuery({
    queryKey: ['portal-employee-me'],
    queryFn: async () => {
      try {
        const r = await apiClient.get("v1/employees/me");
        return r.data;
      } catch (err) {
        if (err.response?.status === 404) return null;
        throw err;
      }
    },
  });

  const employeeId = employeeQ.data?.id ?? null;
  const noRecord = employeeQ.isSuccess && employeeQ.data === null;

  const scheduleQ = useQuery({
    queryKey: ['portal-schedule-assignments', employeeId, isoDate(weekStart)],
    queryFn: async () => {
      const weekEnd = addDays(weekStart, 6);
      const res = await apiClient.get(
        `v1/schedule-assignments/?employee_id=${employeeId}&start_date=${isoDate(weekStart)}&end_date=${isoDate(weekEnd)}&limit=50`
      );
      return res.data.items ?? res.data ?? [];
    },
    enabled: !!employeeId,
  });

  useEffect(() => {
    if (scheduleQ.isError) toast.error(t("portal.scheduleView.loadError"));
  }, [scheduleQ.isError, t]);

  const assignments = scheduleQ.data ?? [];

  const DAYS = [
    t("portal.scheduleView.days.mon"),
    t("portal.scheduleView.days.tue"),
    t("portal.scheduleView.days.wed"),
    t("portal.scheduleView.days.thu"),
    t("portal.scheduleView.days.fri"),
    t("portal.scheduleView.days.sat"),
    t("portal.scheduleView.days.sun"),
  ];

  // Map JS getDay() (0=Sunday) to the backend week_day enum values
  const WEEKDAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const slotForDay = (assignment, day) => {
    const wd = WEEKDAY_KEYS[day.getDay()];
    return (assignment.time_slots || []).find((ts) => ts.week_day === wd) || null;
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const getAssignmentsForDay = (day) => {
    const iso = isoDate(day);
    return assignments.filter((a) => {
      const start = a.start_date ?? a.effective_date;
      const end = a.end_date ?? a.effective_date;
      return (!start || start <= iso) && (!end || end >= iso);
    });
  };

  const today = isoDate(new Date());

  if (employeeQ.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (noRecord) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <AlertCircle className="w-10 h-10 text-muted-foreground" />
          <p className="font-medium">{t("portal.scheduleView.noRecord")}</p>
          <p className="text-sm text-muted-foreground">{t("portal.scheduleView.noRecordDesc")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-lg">
          {t("portal.scheduleView.weekOf")} {weekStart.toLocaleDateString(i18n.language, { day: "numeric", month: "long" })}
        </h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setWeekStart((d) => addDays(d, -7))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))}>
            {t("portal.scheduleView.today")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart((d) => addDays(d, 7))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {scheduleQ.isPending ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map((day, i) => {
            const iso = isoDate(day);
            const isToday = iso === today;
            const dayAssignments = getAssignmentsForDay(day);

            return (
              <div
                key={iso}
                className={`rounded-lg border p-2 min-h-[80px] ${isToday ? "border-primary bg-primary/5" : "bg-card"}`}
              >
                <div className="text-center mb-2">
                  <p className={`text-xs font-medium ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                    {DAYS[i]}
                  </p>
                  <p className={`text-sm font-bold ${isToday ? "text-primary" : ""}`}>
                    {day.getDate()}
                  </p>
                </div>
                <div className="space-y-1">
                  {dayAssignments.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-1">—</p>
                  ) : (
                    dayAssignments.map((a) => {
                      const slot = slotForDay(a, day);
                      const name = a.schedule?.name ?? a.schedule_name ?? "";
                      return (
                        <div
                          key={a.id}
                          className="bg-primary/10 rounded px-1 py-0.5 text-xs text-primary"
                          title={name}
                        >
                          <span className="block truncate">{name}</span>
                          {slot && (
                            <span className="block text-[10px] font-semibold text-primary/80">
                              {slot.start_time} – {slot.end_time}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!scheduleQ.isPending && assignments.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <Calendar className="w-10 h-10 text-muted-foreground" />
            <p className="font-medium">{t("portal.scheduleView.noSchedule")}</p>
            <p className="text-sm text-muted-foreground">{t("portal.scheduleView.noScheduleDesc")}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MonPlanningPerso;
